import { useCallback, useEffect, useRef, useState } from 'react';
import { lessonRunsApi } from '../../services/apiV2';

type Draft = Record<string, any>;
type Checkpoint = { expected_revision: number; request_id: string; context: string };
type Recovery = { payload: Draft; revision: number; context: string; pending?: { payload: Draft; checkpoint: Checkpoint } };
type Session = Recovery & { key: string; owner: number; run: number; step: number; active: boolean; ready: boolean; blocked: boolean; accountChanged?: boolean; submitted?: boolean; busy: boolean; acknowledged: string; timer?: ReturnType<typeof setTimeout> };
export const studioDraftKey = (owner: number, run: number, step: number) => `studio_draft_v2:${owner}:${run}:${step}`;
const fingerprint = (value: Draft) => JSON.stringify(value || {});
export const formatStudioRecovery = (draft: Draft | null): string => Object.entries(draft || {}).map(([name, value]) => {
  const label = name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, first => first.toUpperCase());
  const text = value && typeof value === 'object' ?
    (Array.isArray(value) ? value.map(item => typeof item === 'object' ? formatStudioRecovery(item) : String(item ?? '')).join('\n') : formatStudioRecovery(value)) : String(value ?? '');
  return `${label}: ${text}`;
}).join('\n\n');

/** One queue per mounted owner/run/step. Failed requests retain their identity. */
export function useStudioDraft(owner: number | undefined, run: number | undefined, step: number | undefined,
  enabled: boolean, payload: Draft, restore: (draft: Draft) => void, submitted = false) {
  const current = useRef<Session | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState<Draft | null>(null);
  const [backup, setBackup] = useState<Draft | null>(null);
  const [loadedKey, setLoadedKey] = useState('');
  const [uiKey, setUiKey] = useState('');
  const [reload, setReload] = useState(0);
  const restoreRef = useRef(restore);
  restoreRef.current = restore;
  const key = enabled && owner && run && step ? studioDraftKey(owner, run, step) : '';

  const blockChangedAccount = useCallback((s: Session) => {
    s.blocked = true; s.accountChanged = true; clearTimeout(s.timer);
    restoreRef.current({}); setConflict(null); setBackup(null);
    setStatus('Account changed.');
    setError('The signed-in account changed. Reload this page before continuing. Previous work remains saved under its original account.');
  }, []);

  const persist = useCallback((s: Session) => {
    try {
      const record: Recovery = { payload: s.payload, revision: s.revision, context: s.context, pending: s.pending };
      localStorage.setItem(s.key, JSON.stringify(record));
      return true;
    } catch {
      if (s.active) setError('Browser recovery storage is unavailable. Keep this page open until a server save succeeds.');
      return false;
    }
  }, []);

  const flush = useCallback(async (s: Session) => {
    if (!s.active || !s.ready || s.blocked || s.busy) return;
    s.busy = true;
    try {
      while (s.active && !s.blocked && (s.pending || fingerprint(s.payload) !== s.acknowledged)) {
        if (!s.pending) s.pending = { payload: s.payload, checkpoint: {
          expected_revision: s.revision, context: s.context,
          request_id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        } };
        persist(s);
        if (s.active) setStatus('Saving draft…');
        const request = s.pending;
        const response = await lessonRunsApi.saveStudioDraft(s.run, s.step, request.payload, { ...request.checkpoint, expected_owner_id: s.owner });
        s.revision = response.revision;
        s.acknowledged = fingerprint(request.payload);
        s.pending = undefined;
        // Do not overwrite a newer tab's recovery record after leaving this step.
        if (s.active && !s.blocked) {
          persist(s);
          setError('');
          setStatus(fingerprint(s.payload) === s.acknowledged ? 'Draft saved to server.' : 'Changes waiting to save.');
        }
      }
    } catch (err: any) {
      if (s.active) {
        if (err?.response?.data?.code === 'draft_account_changed') {
          blockChangedAccount(s); return;
        }
        setStatus('Draft not saved to server.');
        setError(err?.response?.data?.error || 'Save failed. Your recovery copy is retained where browser storage is available. Retry when connected.');
        if (err?.response?.status === 409 || err?.response?.status === 400) {
          s.blocked = true;
          setConflict({});
          try {
            const server = await lessonRunsApi.getStudioDraft(s.run, s.step, s.owner);
            if (s.active) setConflict(server.draft_payload || {});
          } catch { /* Preserve local recovery even if the comparison cannot load. */ }
        }
      }
    } finally { s.busy = false; }
  }, [persist, blockChangedAccount]);

  useEffect(() => {
    setLoadedKey(''); setStatus(''); setError(''); setConflict(null); setBackup(null);
    setUiKey(key);
    if (!key || !owner || !run || !step) { current.current = null; return; }
    const s: Session = { key, owner, run, step, payload: {}, revision: 0, context: '', active: true, ready: false, blocked: false, busy: false, acknowledged: '{}' };
    current.current = s;
    let local: Recovery | null = null;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || 'null');
      const previous = JSON.parse(localStorage.getItem(`${key}:previous`) || 'null');
      if (previous?.payload) setBackup(previous.payload);
      if (parsed && typeof parsed.payload === 'object' && !Array.isArray(parsed.payload) && Number.isInteger(parsed.revision) && typeof parsed.context === 'string') local = parsed;
    } catch { setError('Could not read browser recovery storage.'); }
    const load = async () => {
      setStatus('Checking saved draft…');
      try {
        const server = await lessonRunsApi.getStudioDraft(run, step, owner);
        if (!s.active) return;
        s.revision = server.revision; s.context = server.context;
        s.submitted = server.submitted;
        s.acknowledged = fingerprint(server.draft_payload);
        const localDiffers = local && fingerprint(local.payload) !== s.acknowledged;
        const incompatible = Boolean((local && local.context !== server.context) ||
          (server.saved_context && server.saved_context !== server.context));
        s.payload = localDiffers ? local!.payload : (server.draft_payload || {});
        // A lost-response retry is safe only with its original request identity.
        s.pending = local?.pending;
        s.blocked = Boolean(server.submitted || server.run_active === false || incompatible || (localDiffers && local!.revision !== server.revision && !local?.pending));
        if (s.blocked) {
          setConflict(server.draft_payload || {});
          setError(server.submitted ? 'This step is submitted. Recovery text is available below; it will not replace your submission.' : server.run_active === false ? 'This lesson ended. Your recovery text is available below; it has not replaced the saved server version.' : 'Saved versions or activity content differ. Compare the recovery text before continuing.');
        } else restoreRef.current(s.payload);
        s.ready = true; setLoadedKey(key);
        setStatus(s.blocked ? 'Recovery needs review.' : localDiffers ? 'Recovered changes waiting to save.' : 'Saved draft checked.');
      } catch (err: any) {
        if (!s.active) return;
        if (err?.response?.data?.code === 'draft_account_changed') { blockChangedAccount(s); return; }
        if (local) { s.payload = local.payload; setConflict(local.payload); }
        setError('Could not check the server draft. Reconnect and retry before editing so saved work is not overwritten.');
        setStatus('Draft recovery unavailable.');
      }
    };
    void load();
    const online = () => { if (s.ready) void flush(s); else void load(); };
    const onStorage = (event: StorageEvent) => {
      if (s.accountChanged) return;
      if (event.key === key) {
        s.blocked = true;
        try {
          localStorage.setItem(`${s.key}:recovery:${Date.now()}`, JSON.stringify({ payload: s.payload, context: s.context, revision: s.revision }));
          localStorage.setItem(`${s.key}:previous`, JSON.stringify({ payload: s.payload }));
          setBackup(s.payload);
        } catch { /* The visible copy is still available for manual copying. */ }
        setError('Another tab changed this draft. Copy your work before refreshing to compare saved versions.');
        setStatus('Recovery needs review.');
        try {
          const incoming = JSON.parse(event.newValue || 'null');
          setConflict(incoming?.payload || {});
        } catch { setConflict({}); }
      }
    };
    const interval = setInterval(() => { void flush(s); }, 15000);
    window.addEventListener('online', online);
    window.addEventListener('storage', onStorage);
    return () => {
      s.active = false; clearTimeout(s.timer); clearInterval(interval);
      window.removeEventListener('online', online); window.removeEventListener('storage', onStorage);
    };
  }, [key, owner, run, step, flush, reload, blockChangedAccount]);

  useEffect(() => {
    const s = current.current;
    if (s && submitted) {
      s.submitted = true; s.blocked = true; clearTimeout(s.timer);
      setStatus('Step submitted. Draft retained for recovery.');
    }
  }, [submitted, loadedKey]);

  useEffect(() => {
    const s = current.current;
    if (!s || s.key !== key || loadedKey !== key || !s.ready || s.blocked) return;
    if (fingerprint(payload) === fingerprint(s.payload)) return;
    s.payload = payload; persist(s);
    setStatus('Changes waiting to save.');
    clearTimeout(s.timer);
    s.timer = setTimeout(() => { void flush(s); }, 1000);
  }, [payload, key, loadedKey, persist, flush]);

  const useServerCopy = async () => {
    const s = current.current;
    if (!s || s.busy) return;
    try {
      const server = await lessonRunsApi.getStudioDraft(s.run, s.step, s.owner);
      if (!s.active) return;
      // Keep the displaced text in a separate recovery record before accepting a version.
      localStorage.setItem(`${s.key}:recovery:${Date.now()}`, JSON.stringify({ payload: s.payload, context: s.context, revision: s.revision }));
      localStorage.setItem(`${s.key}:previous`, JSON.stringify({ payload: s.payload }));
      setBackup(s.payload);
      if (server.saved_context && server.saved_context !== server.context) {
        setError('The activity changed after this draft was saved. Your teacher needs to resolve the content change.');
        return;
      }
      s.payload = server.draft_payload || {}; s.revision = server.revision; s.context = server.context;
      s.pending = undefined; s.acknowledged = fingerprint(s.payload); s.submitted = server.submitted;
      s.blocked = Boolean(server.submitted || server.run_active === false); s.ready = true;
      persist(s);
      if (!server.submitted) restoreRef.current(s.payload);
      setLoadedKey(s.key); setConflict(null); setError(''); setStatus('Server version selected. Previous local text retained in browser recovery storage.');
    } catch (err: any) {
      if (err?.response?.data?.code === 'draft_account_changed') blockChangedAccount(s);
      else setError('Could not preserve recovery text and load the server copy. Copy your text manually before retrying.');
    }
  };
  return { status: uiKey === key ? status : '', error: uiKey === key ? error : '',
    conflict: uiKey === key ? conflict : null, backup: uiKey === key ? backup : null,
    ready: !key || (loadedKey === key && !current.current?.accountChanged && (!current.current?.blocked || current.current?.submitted)),
    useServerCopy,
    retry: () => { const s = current.current; if (s?.ready && !s.blocked) void flush(s); else setReload(value => value + 1); },
    recovery: current.current?.key === key && !current.current?.accountChanged ? current.current?.payload : null };
}
